import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/pages/designPreview/PreviewVisuals";
import { CAPABILITY_ORDER, themeOf } from "@/pages/designPreview/previewTheme";
import { CapabilityStage, SystemAssembly } from "@/pages/designPreview/capabilitiesScenes";

/* Isolated kit for /design-preview/capabilities only. Nothing here is
   imported by Authority / Guides / Guide Detail, and this file does not
   modify any shared component or stylesheet — see capabilities.css. */

function MiniIcon({ kind, accent = "currentColor", size = 16, className = "" }) {
  const box = { width: size, height: size, viewBox: "0 0 16 16", className, "aria-hidden": true };
  if (kind === "check") {
    return (
      <svg {...box}>
        <path d="M3 8.5 L6.5 12 L13 4.5" fill="none" stroke={accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "dash") {
    return (
      <svg {...box}>
        <path d="M3.5 8 H12.5" fill="none" stroke={accent} strokeWidth="1.8" strokeLinecap="round" opacity="0.6" />
      </svg>
    );
  }
  return (
    <svg {...box}>
      <path d="M4 6 L8 10 L12 6" fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function StatStrip({ stats = [] }) {
  return (
    <div className="dpcap-stats">
      {stats.map((s, i) => (
        <div className="dpcap-stat" key={s.label}>
          <span className="dpcap-stat__dot" style={{ background: CAPABILITY_ORDER[i] ? themeOf(CAPABILITY_ORDER[i]).hue : "#fff" }} />
          <strong>{s.value}</strong>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

const TAB_FEATURES = {
  website: ["반응형 UI/UX 설계", "브랜드 아이덴티티 반영", "검색엔진 기본 최적화 반영"],
  webdev: ["회원·권한·결제 등 맞춤 로직", "API/DB 설계부터 배포까지", "기존 서비스와의 연동"],
  saas: ["멀티테넌시 · 이용권 구조", "결제/구독 플로우 설계", "관리자·고객 화면 분리 설계"],
  admin: ["콘텐츠·주문·문의 통합 관리", "역할별 권한 분리", "CMS/CRM 커스텀 필드"],
  ai: ["제출·분석 이력이 남는 AI 기능", "상담/문의 자동 응대", "콘텐츠 생성 및 요약 지원"],
  automation: ["문의 알림 자동화", "반복 업무 워크플로 연결", "외부 서비스 연동(카카오·메일 등)"],
  search: ["SEO·AEO·GEO 구조 설계", "구조화 데이터(JSON-LD) 반영", "가이드 콘텐츠 연계"],
};

// Organic wipe transition on scene switch — grounded in a verified
// AnimMaster page-transition reference (solid panel sweeps across on a
// skew, content swaps at full coverage, panel clears to reveal the new
// scene), reinterpreted in monochrome. Content underneath is real DOM the
// whole time; the wipe is a decorative overlay with pointer-events: none.
function useWipeSwitch(initialId) {
  const [activeId, setActiveIdState] = useState(initialId);
  const [wiping, setWiping] = useState(false);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const select = (nextId) => {
    if (nextId === activeId) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setActiveIdState(nextId);
      return;
    }
    setWiping(true);
    timers.current.push(setTimeout(() => setActiveIdState(nextId), 320));
    timers.current.push(setTimeout(() => setWiping(false), 650));
  };

  return { activeId, wiping, select };
}

export function CapabilityTabs({ layers = [] }) {
  const ordered = CAPABILITY_ORDER.map((id) => layers.find((row) => row.id === id)).filter(Boolean);
  const list = ordered.length ? ordered : layers;
  const { activeId, wiping, select } = useWipeSwitch(list[0]?.id);
  const active = list.find((row) => row.id === activeId) || list[0];
  const theme = active ? themeOf(active.id) : null;

  if (!active) return null;

  return (
    <div className="dpcap-tabs" style={{ "--dp-node-hue": theme.hue }}>
      <div className="dpcap-tabs__bar" role="tablist" aria-label="DSITESOLUTION 기술 레이어">
        {list.map((row) => {
          const t = themeOf(row.id);
          const isOn = row.id === activeId;
          return (
            <button
              key={row.id}
              type="button"
              role="tab"
              aria-selected={isOn}
              className={`dpcap-tabs__btn${isOn ? " is-on" : ""}`}
              style={{ "--dp-node-hue": t.hue }}
              onClick={() => select(row.id)}
            >
              <Icon kind={row.id} accent={t.hue} size={20} className="dpcap-tabs__icon" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="dpcap-tabs__panel">
        <div className="dpcap-tabs__copy">
          <p className="dp-kicker">0{list.findIndex((r) => r.id === active.id) + 1} / 0{list.length}</p>
          <h3>{active.label}</h3>
          <p>{active.summary}</p>
          <ul className="dpcap-checklist">
            {(TAB_FEATURES[active.id] || []).map((f) => (
              <li key={f}><MiniIcon kind="check" accent={theme.hue} size={16} /><span>{f}</span></li>
            ))}
          </ul>
          <div className="dpcap-tabs__links">
            {active.href ? <Link to={active.href}>서비스 자세히 →</Link> : null}
            {(active.guide_hrefs || []).slice(0, 1).map((href) => (
              <Link key={href} to={href}>관련 가이드 →</Link>
            ))}
          </div>
        </div>
        <div className="dpcap-tabs__stage-wrap">
          <CapabilityStage id={active.id} key={active.id} />
          <div className={`dpcap-wipe${wiping ? " is-active" : ""}`} aria-hidden="true">
            <span className="dpcap-wipe__band" />
          </div>
        </div>
      </div>
    </div>
  );
}

const FLOW_STEPS = [
  { id: "website", title: "기획 · 설계", body: "업종별 정보 구조와 사용자 동선을 먼저 설계합니다." },
  { id: "webdev", title: "디자인 · 개발", body: "브랜드 UI와 맞춤형 프론트엔드/백엔드를 함께 만듭니다." },
  { id: "admin", title: "시스템 연동", body: "관리자·CMS·SaaS 등 운영에 필요한 시스템을 붙입니다." },
  { id: "ai", title: "AI · 자동화 적용", body: "응대, 문의, 반복 업무에 AI와 자동화를 연결합니다." },
  { id: "search", title: "검색 최적화", body: "SEO·AEO·GEO 구조를 함께 반영해 검색에 노출시킵니다." },
];

// Scroll-through percentage (0–1) of an element — same proven formula used
// across this preview: 0 when the element's top reaches the viewport
// bottom, 1 when its bottom passes the viewport top.
export function useScrollThrough(ref) {
  const [progress, setProgress] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduceMotion(reduce);
    if (reduce) {
      setProgress(1);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const total = rect.height + vh;
        const passed = vh - rect.top;
        setProgress(Math.max(0, Math.min(1, passed / total)));
        raf = null;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);

  return { progress, reduceMotion };
}

export function ProcessFlow() {
  return <SystemAssembly steps={FLOW_STEPS} />;
}

export function ComparisonTable({ rows = [] }) {
  if (!rows.length) return null;
  return (
    <div className="dpcap-table-wrap">
      <table className="dpcap-table">
        <thead>
          <tr>
            <th scope="col">항목</th>
            <th scope="col">일반 제작사</th>
            <th scope="col">DSITESOLUTION</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.item}>
              <th scope="row">{row.item}</th>
              <td><MiniIcon kind="dash" size={14} className="dpcap-table__mark" /><span>{row.typical}</span></td>
              <td className="dpcap-table__on"><MiniIcon kind="check" accent="#34d399" size={14} className="dpcap-table__mark" /><span>{row.dsite}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TrustBadges({ facts = [] }) {
  if (!facts.length) return null;
  return (
    <div className="dpcap-trust">
      <div className="dpcap-trust__badges">
        {facts.slice(0, 6).map((fact, i) => (
          <span className="dpcap-trust__badge" key={fact.public_title || i}>{fact.public_title}</span>
        ))}
      </div>
      <Link to="/design-preview/authority" className="dpcap-trust__link">Authority 전체 보기 →</Link>
    </div>
  );
}

const FAQ_ITEMS = [
  { q: "웹사이트 제작과 맞춤형 웹개발은 뭐가 다른가요?", a: "웹사이트는 브랜드를 알리는 공개 화면이고, 맞춤형 웹개발은 회원·결제·API 등 로직이 들어간 프로그램입니다. 필요에 따라 두 가지를 함께 구축합니다." },
  { q: "AI 기능은 실제로 어떻게 적용되나요?", a: "문의 자동 응대, 콘텐츠 생성/요약, 제출 이력이 남는 분석 기능 등 실사용 흐름에 맞춰 붙입니다. 장식용 챗봇이 아니라 기록이 남는 기능입니다." },
  { q: "이미 운영 중인 사이트도 시스템으로 연결할 수 있나요?", a: "네. 기존 사이트/관리자 구조를 진단한 뒤 어떤 레이어를 추가·연결할지 먼저 정리합니다." },
  { q: "SEO·AEO·GEO는 제작 단계에서 어떻게 반영되나요?", a: "설계 단계부터 heading 구조, 구조화 데이터, 검색 질문에 대한 답변형 콘텐츠를 함께 설계합니다." },
];

export function FaqAccordion() {
  return (
    <div className="dpcap-faq">
      {FAQ_ITEMS.map((item) => (
        <details className="dpcap-faq__item" key={item.q}>
          <summary>
            <span>{item.q}</span>
            <MiniIcon kind="chevron" size={16} className="dpcap-faq__chevron" />
          </summary>
          <p>{item.a}</p>
        </details>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Build flow — replaces the old 7-card catalogue. Reads as one system  */
/* being assembled in order, not seven independent products: each tier */
/* carries a role, tiers vary in weight (solo vs paired), and every     */
/* item links out to real evidence (Service / Guide / Portfolio) so it  */
/* reads as "we have built this" rather than "we could build this".    */
/* Monochrome throughout — the rail itself is the only "graphic",       */
/* because it is the one visual that actually explains the relationship */
/* between stages (sequence + dependency), not decoration.              */
/* ------------------------------------------------------------------ */

const FLOW_TIERS = [
  { role: "시작 지점", hint: "브랜드를 알리는 첫 화면", ids: ["website"] },
  { role: "핵심 구축", hint: "로직이 들어간 맞춤 개발", ids: ["webdev"] },
  { role: "운영 시스템", hint: "데이터를 다루는 백엔드 레이어", ids: ["admin", "saas"] },
  { role: "지능형 확장", hint: "판단과 반복 업무를 대신 처리", ids: ["ai", "automation"] },
  { role: "검색 노출", hint: "지금까지의 결과가 발견되는 지점", ids: ["search"], outcome: true },
];

function EvidenceRow({ row }) {
  const guideHref = (row.guide_hrefs || [])[0];
  return (
    <div className="dpcap-evidence">
      {row.href ? <Link to={row.href}>Service</Link> : null}
      {row.href && guideHref ? <span aria-hidden="true">·</span> : null}
      {guideHref ? <Link to={guideHref}>Guide</Link> : null}
      <span aria-hidden="true">·</span>
      <Link to="/portfolio/brand-web">Portfolio</Link>
    </div>
  );
}

// ±2.5° pointer-tracked tilt on a paired-tier box — real content, not a
// decorative object. Skipped for touch pointers and reduced motion.
function usePairTilt() {
  const ref = useRef(null);
  const raf = useRef(null);
  const disabled = useRef(false);

  useEffect(() => {
    disabled.current =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches;
  }, []);

  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el || disabled.current) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty("--dpcap-tilt-x", `${(-py * 2.5).toFixed(2)}deg`);
      el.style.setProperty("--dpcap-tilt-y", `${(px * 2.5).toFixed(2)}deg`);
      raf.current = null;
    });
  };

  const onMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--dpcap-tilt-x", "0deg");
    el.style.setProperty("--dpcap-tilt-y", "0deg");
  };

  return { ref, onMouseMove, onMouseLeave };
}

function PairedItems({ items }) {
  const tilt = usePairTilt();
  return (
    <div
      className="dpcap-flow2__items"
      ref={tilt.ref}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <span className="dpcap-flow2__pulse" aria-hidden="true" />
      {items.map((row) => (
        <div className="dpcap-flow2__item" key={row.id}>
          <h3>{row.label}</h3>
          <p>{row.summary}</p>
          <EvidenceRow row={row} />
        </div>
      ))}
    </div>
  );
}

export function BuildFlow({ layers = [], metric }) {
  const byId = (id) => layers.find((row) => row.id === id);
  const metricValue = metric?.metric_value || "832";
  const railRef = useRef(null);
  const { progress } = useScrollThrough(railRef);

  return (
    <div className="dpcap-flow2">
      <p className="dpcap-flow2__lede">
        {metricValue}건의 프로젝트를 거치며 다져진 순서입니다. 브랜드를 보여주는 화면에서 시작해 데이터를 다루는
        시스템을 붙이고, 마지막에 검색에서 발견되게 만듭니다 — 따로 파는 기능이 아니라 한 시스템의 단계입니다.
      </p>
      <div className="dpcap-flow2__rail" ref={railRef}>
        <div className="dpcap-flow2__rail-fill" style={{ height: `${progress * 100}%` }} aria-hidden="true" />
        {FLOW_TIERS.map((tier, i) => {
          const items = tier.ids.map(byId).filter(Boolean);
          if (!items.length) return null;
          const isPaired = items.length > 1;
          return (
            <div
              className={`dpcap-flow2__tier${isPaired ? " is-paired" : ""}${tier.outcome ? " is-outcome" : ""}`}
              key={tier.role}
            >
              <div className="dpcap-flow2__marker">
                <span className="dpcap-flow2__dot" />
              </div>
              <div className="dpcap-flow2__body">
                <p className="dpcap-flow2__role">0{i + 1} · {tier.role}</p>
                <p className="dpcap-flow2__hint">{tier.hint}</p>
                {isPaired ? (
                  <PairedItems items={items} />
                ) : (
                  <div className="dpcap-flow2__items">
                    {items.map((row) => (
                      <div className="dpcap-flow2__item" key={row.id}>
                        <h3>{row.label}</h3>
                        <p>{row.summary}</p>
                        <EvidenceRow row={row} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
